import React from 'react';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { Check, Prohibit } from 'phosphor-react';
import { ILegalizacao, TipoLegalizacao } from '../../../../hooks/useLegalizacao';
import {
  camposEfetivosDaEtapa,
  checklistEfetivoDaEtapa,
  etapaDoPlaybook,
  PlaybookCampo,
  regimeDoProcesso,
  tiposAlteracaoDoProcesso,
  itemSatisfeito,
  dispensaDoItem,
  rotuloDoMotivo,
  CHAVE_EXIGENCIA,
  CHAVE_SIMPLES,
  CHAVE_CLIENTE,
  motivoDoSimples,
  textoDaExigencia,
} from '../legalizacaoPlaybook';

interface EtapaReadOnlyProps {
  legalizacao: ILegalizacao;
  tipo: TipoLegalizacao;
  /** Título da coluna/etapa a renderizar. */
  coluna: string;
}

const Titulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    fontSize="10px"
    color="gray.400"
    fontWeight="700"
    textTransform="uppercase"
    letterSpacing="0.08em"
    mb={2}
  >
    {children}
  </Text>
);

/**
 * Campos + checklist de UMA etapa, somente leitura.
 *
 * Extraído do `renderReadOnly` que vivia dentro do `LegalizacaoDrawer` (usado no
 * "peek" de outra coluna). Virou componente porque a lista de arquivados também
 * precisa exibir a etapa — e ela NÃO tem card ativo, só o processo. Como tudo
 * aqui sai de `legalizacao.estado_etapas`, o card nunca foi necessário.
 *
 * O checklist é o EFETIVO (registry: subpassos de viabilidade por junta, órgãos
 * pós-registro), não o estático do playbook — senão a leitura mostraria itens
 * que aquela etapa nunca pediu naquela junta.
 */
export const EtapaReadOnly: React.FC<EtapaReadOnlyProps> = ({ legalizacao, tipo, coluna }) => {
  const et = etapaDoPlaybook(tipo, coluna);
  const est = ((legalizacao.estado_etapas as Record<string, Record<string, unknown>> | null)?.[
    coluna
  ] ?? {}) as Record<string, unknown>;

  // "Será cliente?" sem resposta = processo que passou do Deferido antes da pergunta (27/10): não mostra pendente.
  const checklistDaCol = checklistEfetivoDaEtapa(
    tipo,
    coluna,
    legalizacao.junta_uf,
    legalizacao.municipio,
    tiposAlteracaoDoProcesso(legalizacao.estado_etapas),
    regimeDoProcesso(legalizacao.estado_etapas),
  ).filter(c => c.key !== CHAVE_CLIENTE || typeof est[c.key] === 'string');

  const valor = (c: PlaybookCampo): string => {
    if (c.origem === 'display') return c.valorDisplay ?? '—';
    const src =
      c.origem === 'subtype'
        ? (legalizacao as unknown as Record<string, unknown>)[c.key]
        : est[c.key];
    return src != null && src !== '' ? String(src) : '—';
  };

  // Campos da UF (sem os de DBE onde a abertura não tem DBE), mas o que já foi preenchido continua à vista.
  const daUf = new Set(
    camposEfetivosDaEtapa(tipo, coluna, legalizacao.junta_uf, legalizacao.municipio).map(
      c => c.key,
    ),
  );
  const campos = (et?.campos ?? []).filter(c => daUf.has(c.key) || valor(c) !== '—');

  const semConteudo = !campos.length && !checklistDaCol.length;

  if (semConteudo) {
    return (
      <Text fontSize="11px" color="gray.400">
        Esta etapa não tem campos nem checklist.
      </Text>
    );
  }

  return (
    <Stack spacing={3}>
      {campos.length ? (
        <Box>
          <Titulo>Campos</Titulo>
          <Stack spacing={2}>
            {campos.map(c => (
              <Flex key={c.key} justify="space-between" gap={4}>
                <Text fontSize="11px" color="gray.500">
                  {c.label}
                </Text>
                <Text fontSize="13px" color="gray.800" fontWeight="500" textAlign="right">
                  {valor(c)}
                </Text>
              </Flex>
            ))}
          </Stack>
        </Box>
      ) : null}

      {checklistDaCol.length ? (
        <Box>
          <Titulo>Checklist</Titulo>
          <Stack spacing={2}>
            {/* TRÊS estados, não dois — feito, DISPENSADO e pendente.
                Esta lista passou a usar `itemSatisfeito` junto com o resto do módulo, e
                item dispensado é satisfeito: ganhava o mesmo ✓ verde de quem realmente
                fez o trabalho. Numa etapa já concluída, que é justamente o que esta
                visão mostra, isso apagava a diferença entre "feito" e "não se aplicava".
                É o mesmo erro que o drawer cometia, e o motivo de a dispensa ter cor e
                selo próprios lá. */}
            {checklistDaCol.map(chk => {
              // A pergunta da exigência não tem dispensa (nem nos gates); a do Simples tem.
              const pergunta =
                chk.key === CHAVE_EXIGENCIA ||
                chk.key === CHAVE_SIMPLES ||
                chk.key === CHAVE_CLIENTE;
              const dispensa = chk.key === CHAVE_EXIGENCIA ? null : dispensaDoItem(est, chk.key);
              const feito = !dispensa && itemSatisfeito(est, chk.key);
              // Mesma razão do drawer: três estados excludentes numa linha só. Nomear a
              // cor uma vez evita repetir a cadeia de condições — e o lint proíbe
              // ternário aninhado justamente por isso.
              const corDoRotulo = (() => {
                if (dispensa) return 'orange.700';
                return feito ? 'gray.400' : 'gray.700';
              })();
              return (
                <Flex key={chk.key} align="flex-start" gap={2}>
                  {(() => {
                    if (dispensa)
                      return (
                        <Box mt="1px">
                          <Prohibit
                            size={14}
                            weight="bold"
                            color="var(--chakra-colors-orange-400)"
                          />
                        </Box>
                      );
                    if (feito)
                      return (
                        <Box mt="1px">
                          <Check size={14} weight="bold" color="var(--chakra-colors-green-500)" />
                        </Box>
                      );
                    return (
                      <Box
                        mt="2px"
                        w="14px"
                        h="14px"
                        flexShrink={0}
                        borderRadius="sm"
                        borderWidth="1.5px"
                        borderColor="gray.300"
                      />
                    );
                  })()}
                  <Box minW={0}>
                    <Text fontSize="13px" color={corDoRotulo}>
                      {chk.label}
                      {/* A exigência é resposta, não marcação: mostra qual, e o que foi pedido. */}
                      {pergunta && typeof est[chk.key] === 'string' && ` ${est[chk.key]}`}
                    </Text>
                    {chk.key === CHAVE_SIMPLES &&
                      est[CHAVE_SIMPLES] === 'Não' &&
                      motivoDoSimples(est) && (
                        <Text
                          fontSize="11px"
                          color="orange.700"
                          lineHeight="1.4"
                          whiteSpace="pre-wrap"
                        >
                          {motivoDoSimples(est)}
                        </Text>
                      )}
                    {chk.key === CHAVE_EXIGENCIA &&
                      est[CHAVE_EXIGENCIA] === 'Sim' &&
                      textoDaExigencia(est) && (
                        <Text
                          fontSize="11px"
                          color="orange.700"
                          lineHeight="1.4"
                          whiteSpace="pre-wrap"
                        >
                          {textoDaExigencia(est)}
                        </Text>
                      )}
                    {/* O MOTIVO vem junto. Quem abre uma etapa concluída está
                        reconstituindo o que aconteceu — saber que o item foi dispensado
                        sem saber por quê deixa a pergunta no mesmo lugar. */}
                    {dispensa && (
                      <Text fontSize="11px" color="orange.600" lineHeight="1.4">
                        {rotuloDoMotivo(dispensa.motivo)}
                        {dispensa.obs ? ` — ${dispensa.obs}` : ''}
                        {dispensa.por ? ` · ${dispensa.por}` : ''}
                      </Text>
                    )}
                  </Box>
                </Flex>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
};
